<template>
  <div ref="sectionRef" class="fixed left-0 top-0 z-50 h-full min-h-screen w-full overflow-auto bg-white">
    <div class="sticky top-0 bg-blue py-5 lg:py-6 z-50">
      <div class="container flex items-center justify-between gap-2">
        <Logo :color="'white'" />
        <div class="flex items-center gap-[6px] text-sm text-white sm:text-lg lg:gap-6 lg:text-xl">
          {{ $t('inquiryForm.headerNavText') }}
          <NuxtLink :to="localePath('/')">
            <Icon name="close" color="white" />
          </NuxtLink>
        </div>
      </div>
    </div>
    <div class="grid-default container min-h-[calc(100vh-76px)] relative z-10">
      <div
        class="before:content[''] relative -z-1 col-span-6 -mx-4 rounded-b-3xl bg-light-gray-200 px-4 pb-24 pt-8 before:absolute before:right-0 before:top-0 before:-z-1 before:hidden before:h-full before:w-screen before:rounded-3xl before:bg-light-gray-200 lg:fixed lg:top-0 lg:mx-0 lg:h-screen lg:max-w-[230px] lg:items-start lg:rounded-none lg:pb-14 lg:pl-0 lg:pr-10 lg:pt-44 before:lg:block xl:max-w-[290px]"
      >
        <div
          class="top-0 flex flex-col items-center justify-between lg:h-[calc(100vh-240px)] lg:items-start"
        >
          <InquiryNav />
          <div class="hidden flex-col gap-3 text-xl font-light text-blue-950 lg:flex lg:text-2xl">
            {{ $t('inquiryForm.contactTitle') }}
            <a
              :href="`tel:${data.contactNumber}`"
              class="w-fit rounded-[64px] bg-blue-100 px-5 py-[6px] text-xl font-semibold text-blue-800"
              >{{ data.contactNumber }}</a
            >
          </div>
        </div>
      </div>
      <div
        ref="navRef"
        class="col-span-6 mt-8 min-h-screen lg:col-start-4 lg:col-end-13 lg:mt-0 lg:pb-14 lg:pt-24"
      >
        <form
          id="inquiry-form"
          name="Inquiry form"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          novalidate="true"
          method="POST"
          class="min-h-[calc(100vh-330px)]"
          @submit.prevent="submitForm"
        >
          <input type="hidden" name="form-name" value="Inquiry form" />
          <TransitionGroup name="inquiry-fade">
            <InquiryMaterialSelect
              v-show="store.activeStep === 1"
              key="1"
              v-model="formData.productDetails"
            />
            <InquiryProductDetails
              v-show="store.activeStep === 2"
              key="2"
              v-model="formData.productDetails"
            />
            <InquiryUserDetails
              v-show="store.activeStep === 3"
              key="3"
              v-model="formData.userDetails"
              @form:validated="isFormValid = true"
              @form:invalid="isFormValid = false"
            />
          </TransitionGroup>
          <div class="ml-auto mt-8 w-fit lg:mt-10">
            <Button
              v-if="store.activeStep > 1"
              variant="link"
              type="button"
              @click="handlePrevStep"
              >{{ $t('inquiryForm.previousText') }}</Button
            >
            <Button
              v-if="store.activeStep !== LAST_STEP"
              type="button"
              icon
              :disabled="!isStepValid"
              @click="handleNextStep"
              >{{ $t('inquiryForm.buttonText') }}</Button
            >

            <Button v-else type="submit" icon :disabled="!isStepValid">{{
              $t('inquiryForm.buttonText')
            }}</Button>
          </div>
        </form>
        <div
          class="mb-10 mt-8 flex flex-col gap-3 rounded-3xl bg-blue-800 p-5 text-center text-xl font-light text-white lg:hidden lg:text-2xl"
        >
          {{ $t('inquiryForm.contactTitle') }}
          <a
            :href="`tel:${data.contactNumber}`"
            class="mx-auto w-fit rounded-[64px] bg-blue-100 px-5 py-[6px] text-xl font-semibold text-blue-800"
            >{{ data.contactNumber }}</a
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useInquiryStore } from '~/stores/InquiryStore'
import { DatoCmsCategoryId } from '~/types/categories'
import { SectionInquiryFormRecord } from '~/types/generated'
import { InquiryFormData } from '~/types/inquiry'

const { locale, t } = useI18n()
const localePath = useLocalePath()
const { bodyLock, bodyUnlock } = useBodyLock()
const store = useInquiryStore()
const sectionRef = ref<HTMLElement | null>(null)
const isFormValid = ref(false)
const LAST_STEP = 3
const CATEGORIES = [
  {
    id: DatoCmsCategoryId.KOVINARSKI,
    name: 'Kovinarski program'
  },
  {
    id: DatoCmsCategoryId.GRADBENI,
    name: 'Gradbeni program'
  },
  {
    id: DatoCmsCategoryId.LESNI,
    name: 'Lesni program'
  }
]

defineProps({
  data: {
    type: Object as PropType<SectionInquiryFormRecord>,
    default: () => {}
  }
})

const formData = reactive<InquiryFormData>({
  productDetails: {
    category: store.preselectedCategory.programCategory ?? '',
    product: store.preselectedProduct.product ?? '',
    productDetailMessage: '',
    ProductMaterial: '',
    Dimensions: undefined,
    DiscDiameter: undefined,
    MountingHoleSize: undefined,
    Granulation: undefined,
    GrainType: undefined,
    SurfaceType: undefined,
    Quantity: undefined,
    NumberOfHoles: undefined,
    ClampingMethod: undefined
  },
  userDetails: {
    Name: '',
    Surname: '',
    Telephone: '',
    Email: '',
    CompanyName: '',
    TaxNumber: '',
    PrivacyAccepted: true,
    Subscribed: false,
    recaptchaValue: ''
  }
})

const isStepValid = computed(() => {
  switch (store.activeStep) {
    case 1:
      return !!formData.productDetails.product
    case 2:
      return !!formData.productDetails.productDetailMessage
    case 3:
      return isFormValid.value
    default:
      return false
  }
})

if (!store.categories.length) {
  store.fetchCategories(locale.value)
}

if (!store.products.length) {
  store.fetchProducts(locale.value)
}

onMounted(() => {
  bodyLock()
})

onUnmounted(() => {
  bodyUnlock()
})

const URIEncode = (data: any) => {
  return Object.keys(data)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&')
}

async function submitForm() {
  const state = {
    ...formData.productDetails,
    ...formData.userDetails
  }

  const category = store.categories.find(
    el => el.programCategory === formData.productDetails.category
  )

  const tagCategory = CATEGORIES.find(el => el.id === category?.id)

  const payload = {
    email_address: state.Email,
    status: 'subscribed',
    tags: tagCategory && tagCategory.name ? [tagCategory.name] : undefined
  }

  const { error } = await useFetch('/.netlify/functions/mailchimp-form', {
    headers: { 'Content-type': 'application/json' },
    method: 'POST',
    body: toRaw(payload)
  })

  if (error.value) {
    // eslint-disable-next-line no-console
    console.log('error:', error.value)
  }

  try {
    await fetch('/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: URIEncode({
        'form-name': 'Inquiry form',
        ...state
      })
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error)
  } finally {
    await navigateTo(t('inquiryForm.thankYouRoute'))

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
    store.setActiveStep(1)
  }
}

function handleNextStep() {
  store.setActiveStep(store.activeStep + 1)
  sectionRef.value?.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

function handlePrevStep() {
  store.setActiveStep(store.activeStep - 1)
  sectionRef.value?.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}
</script>
